<template>
  <div class="px-4 py-6 sm:px-6 lg:px-8">
    <!-- Back button and header -->
    <div class="flex items-center mb-6">
      <button @click="router.back()"
        class="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
        <i class="text-lg pi pi-arrow-left"></i>
      </button>
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">User Details</h1>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-10">
      <div class="w-10 h-10 rounded-full border-t-2 border-b-2 animate-spin border-primary"></div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-4 mb-6 bg-red-50 rounded-md dark:bg-red-900/20">
      <p class="text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- User details -->
    <div v-else-if="user" class="space-y-6">
      <!-- User profile card -->
      <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
        <div class="p-6">
          <div class="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
            <div class="flex justify-center items-center w-16 h-16 rounded-full bg-primary/10 text-primary">
              <i class="text-3xl pi pi-user"></i>
            </div>
            <div class="flex-1">
              <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ user.name }}</h2>
              <div class="flex flex-col mt-1 sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <i class="mr-1.5 pi pi-envelope"></i>
                  {{ user.email }}
                </div>
                <div class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <i class="mr-1.5 pi pi-phone"></i>
                  {{ user.phone || 'N/A' }}
                </div>
                <div class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <i class="mr-1.5 pi pi-id-card"></i>
                  Role: <span class="ml-1 capitalize">{{ userRoles[user.role] }}</span>
                </div>
                <div class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <i class="mr-1.5 pi pi-calendar"></i>
                  Joined: {{ formatDateTime(user.created_at) }}
                </div>
                <div class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <i class="mr-1.5 pi pi-location"></i>
                  Address: {{ user.address }}
                </div>
              </div>
            </div>
            <div class="flex-shrink-0 flex flex-col items-end gap-2">
              <span
                v-if="user.role === USER_ROLES.BUSINESS && user.business"
                :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': user.business && user.business.is_active,
                  'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400': user.business && !user.business.is_active,
                }"
                class="px-2.5 py-0.5 font-medium rounded-full">
                Store: {{ user.business.is_active ? 'Open' : 'Closed' }}
              </span>
              <span
                v-if="user.role !== USER_ROLES.ADMIN"
                :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': !user.is_deactivated,
                  'bg-amber-100 text-amber-900 dark:bg-amber-900/20 dark:text-amber-300': user.is_deactivated,
                }"
                class="px-2.5 py-0.5 font-medium rounded-full">
                Account: {{ user.is_deactivated ? 'Deactivated' : 'Active' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="user.role !== USER_ROLES.ADMIN" class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
        <div class="p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Account access</h3>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Deactivated users cannot sign in. Their businesses are hidden from public listings.
            </p>
          </div>
          <button
            type="button"
            :disabled="updatingDeactivation"
            @click="user.is_deactivated ? setUserDeactivation(false) : confirmDeactivate = true"
            :class="user.is_deactivated
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
              : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'"
            class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md border border-transparent shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-900">
            {{ updatingDeactivation ? 'Updating…' : (user.is_deactivated ? 'Reactivate account' : 'Deactivate account') }}
          </button>
        </div>
      </div>

      <div v-if="user.bank_account" class="space-y-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Bank Account</h3>

        <!-- Business metrics cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SingleUserDetail :stats="user.bank_account.account_name" label="Account Name" icon="user" />
          <SingleUserDetail :stats="user.bank_account.account_number" label="Account Number" icon="user" />
          <SingleUserDetail :stats="user.bank_account.bank_name" label="Bank Name" icon="user" />
        </div>
      </div>

      <!-- Role-specific metrics -->
      <div v-if="user.role === USER_ROLES.BUSINESS" class="space-y-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Business Metrics</h3>

        <!-- Business metrics cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SingleUserDetail :stats="formatCurrency(userStats.totalRevenue || 0)" label="Total Sales" icon="shopping-cart" />
          <SingleUserDetail :stats="userStats.totalOrders || 0" label="Total Orders" icon="shopping-cart" />
          <SingleUserDetail :stats="userStats.totalPaidOrders || 0" label="Total Paid Orders" icon="shopping-cart" />
          <SingleUserDetail :stats="userStats.totalCustomers || 0" label="Total Customers" icon="users" />
          <SingleUserDetail :stats="formatCurrency(userStats.customerServiceFees || 0)" label="Service Fees Paid By Customer"
            icon="percentage" />
          <SingleUserDetail :stats="formatCurrency(userStats.businessServiceFees || 0)" label="Service Fees Paid By Business"
            icon="percentage" />
          <SingleUserDetail :stats="formatCurrency(userStats.deliveryFees || 0)" label="Delivery Fees Paid"
            icon="percentage" />
        </div>

        <!-- Recent orders for this business -->
        <div class="mt-8">
          <h3 class="mb-4 text-lg font-medium text-gray-900 dark:text-white">Recent Orders</h3>
          <DataTable
            v-if="recentOrders && recentOrders.length > 0"
            :items="recentOrders"
            :columns="orderColumns"
            :pagination="ordersPagination"
            @route-change="handleRowClick"
            @page-change="($page) => handlePageChange($page,'orders')" />
          <p v-else class="text-gray-500 dark:text-gray-400">No recent orders found!</p>
        </div>
      </div>

      <div v-else-if="user.role === USER_ROLES.CUSTOMER" class="space-y-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Customer Metrics</h3>

        <!-- Customer metrics cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SingleUserDetail :stats="userStats.totalOrders || 0" label="Total Orders" icon="shopping-cart" />
          <SingleUserDetail :stats="formatCurrency(userStats.totalSpent || 0)" label="Total Spent" icon="dollar" />
          <SingleUserDetail :stats="userStats.businessesPatronized || 0" label="Businesses Patronized" icon="building" />
          <SingleUserDetail :stats="userStats.completedOrders || 0" label="Completed Orders" icon="check-circle" />
        </div>

        <!-- Most patronized business -->
        <div v-if="userStats.mostPatronizedBusiness"
          class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
          <div class="p-6">
            <h3 class="mb-4 text-lg font-medium text-gray-900 dark:text-white">Most Patronized Business</h3>
            <div class="flex items-center">
              <div class="flex justify-center items-center w-12 h-12 rounded-full bg-primary/10 text-primary">
                <i class="text-xl pi pi-building"></i>
              </div>
              <div class="ml-4">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white">{{ userStats.mostPatronizedBusiness.name
                  }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ userStats.mostPatronizedBusiness.orderCount }} orders · {{
                  formatCurrency(userStats.mostPatronizedBusiness.totalSpent) }} spent
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent orders by this customer -->
        <div class="mt-8">
          <h3 class="mb-4 text-lg font-medium text-gray-900 dark:text-white">Recent Orders</h3>
          <DataTable
            v-if="recentOrders && recentOrders.length > 0"
            :items="recentOrders"
            :columns="orderColumns"
            :pagination="ordersPagination"
            @route-change="handleRowClick"
            @page-change="($page) => handlePageChange($page,'orders')" />
          <p v-else class="text-gray-500 dark:text-gray-400">No recent orders found.</p>
        </div>
      </div>

      <div v-else-if="user.role === USER_ROLES.DRIVER" class="space-y-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Driver Metrics</h3>

        <!-- Driver metrics cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-check-circle text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Orders Delivered</dt>
                    <dd class="flex items-baseline">
                      <div class="text-2xl font-semibold text-gray-900 dark:text-white">{{ userStats.ordersDelivered ||
                        0 }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-inbox text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Orders Assigned</dt>
                    <dd class="flex items-baseline">
                      <div class="text-2xl font-semibold text-gray-900 dark:text-white">{{ userStats.ordersAssigned || 0
                        }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-dollar text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Delivery Earnings</dt>
                    <dd class="flex items-baseline">
                      <div class="text-2xl font-semibold text-gray-900 dark:text-white">{{
                        formatCurrency(userStats.deliveryEarnings || 0) }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-star text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Average Rating</dt>
                    <dd class="flex items-baseline">
                      <div class="text-2xl font-semibold text-gray-900 dark:text-white">{{ userStats.averageRating ||
                        'N/A' }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent deliveries by this driver -->
        <div class="mt-8">
          <h3 class="mb-4 text-lg font-medium text-gray-900 dark:text-white">Recent Deliveries</h3>
          <DataTable
            v-if="recentDeliveries && recentDeliveries.length > 0"
            :items="recentDeliveries"
            :columns="deliveryColumns"
            :pagination="deliveryPagination"
            @route-change="handleRowClick"
            @page-change="($page) => handlePageChange($page,'deliveries')" />
          <p v-else class="text-gray-500 dark:text-gray-400">No recent deliveries found.</p>
        </div>
      </div>

      <div v-else-if="user.role === USER_ROLES.ADMIN" class="space-y-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Admin Activity</h3>

        <!-- Admin metrics cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-calendar text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Last Login</dt>
                    <dd class="flex items-baseline">
                      <div class="text-lg font-semibold text-gray-900 dark:text-white">{{ formatDateTime(userStats.lastLogin
                        || user.created_at) }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-cog text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Actions Performed</dt>
                    <dd class="flex items-baseline">
                      <div class="text-2xl font-semibold text-gray-900 dark:text-white">{{ userStats.actionsPerformed ||
                        0 }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div class="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0 p-3 rounded-md bg-primary/10">
                  <i class="pi pi-shield text-primary"></i>
                </div>
                <div class="flex-1 ml-5 w-0">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Permission Level</dt>
                    <dd class="flex items-baseline">
                      <div class="text-lg font-semibold text-gray-900 dark:text-white">{{ userStats.permissionLevel ||
                        'Standard Admin' }}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ConfirmationModal :show="confirmDeactivate" @close="confirmDeactivate = false">
    <template #title>Deactivate account</template>
    <template #content>
      <p>This user will be signed out of all sessions and cannot log in until reactivated.</p>
    </template>
    <template #footer>
      <SecondaryButton class="mr-2" @click="confirmDeactivate = false">Cancel</SecondaryButton>
      <DangerButton @click="applyDeactivate">Deactivate</DangerButton>
    </template>
  </ConfirmationModal>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/axios';
import DataTable from '@/components/general/DataTable.vue';
import { formatCurrency, formatDateTime, capitalize } from '@/utils';
import SingleUserDetail from '@/views/admin/users/SingleUserDetail.vue';
import { USER_ROLES } from '@/types';
import { paginationDefault, userRoles } from '@/constants';
import { ROUTES } from '@/router/routeNames';
import { useToast } from 'primevue/usetoast';
import ConfirmationModal from '@/components/general/ConfirmationModal.vue';
import SecondaryButton from '@/components/general/SecondaryButton.vue';
import DangerButton from '@/components/general/DangerButton.vue';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const userId = route.params.id;

const user = ref(null);
const userStats = ref({});
const recentOrders = ref([]);
const recentDeliveries = ref([]);
const loading = ref(true);
const error = ref(null);
const updatingDeactivation = ref(false);
const confirmDeactivate = ref(false);

// Pagination objects
const ordersPagination = ref(paginationDefault);

const deliveryPagination = ref(paginationDefault);

const handleRowClick = (data) => {
  router.push({ name: ROUTES.DASHBOARD_ORDER, params: { id: data } });
}

const setUserDeactivation = async (is_deactivated) => {
  updatingDeactivation.value = true;
  try {
    const response = await api.patch(`/api/admin/users/${user.value.id}/deactivation`, { is_deactivated });
    user.value = response.data.user;
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: is_deactivated ? 'Account has been deactivated.' : 'Account has been reactivated.',
      life: 3000,
    });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to update account',
      life: 4000,
    });
  } finally {
    updatingDeactivation.value = false;
    confirmDeactivate.value = false;
  }
};

const applyDeactivate = () => {
  setUserDeactivation(true);
};

const handlePageChange = (page, type) => {

  if (type === 'orders' && user.value.role === USER_ROLES.BUSINESS) {
    fetchRecentOrders(page);
  } else if (type === 'deliveries' && user.value.role === USER_ROLES.DRIVER) {
    fetchRecentDeliveries(page);
  }
}

// Table columns
const orderColumns = [
  { key: 'id', label: 'ID', width: '80px' },
  {
    key: 'business',
    label: 'Business',
    format: (item) => item.business.name
  },
  {
    key: 'customer',
    label: 'Customer',
    format: (item) => item.customer.name
  },
  {
    key: 'total_amount',
    label: 'Amount',
    format: (item) => formatCurrency(item.total_amount)
  },
  {
    key: 'status',
    label: 'Status',
    class: (item) => {
      const statusClasses = {
        'pending': 'text-yellow-600 dark:text-yellow-400',
        'processing': 'text-blue-600 dark:text-blue-400',
        'completed': 'text-green-600 dark:text-green-400',
        'cancelled': 'text-red-600 dark:text-red-400',
      };
      return statusClasses[item.status] || '';
    },
    format: (item) => capitalize(item.status),
  },
  {
    key: 'payment_status',
    label: 'Payment Status',
    format: (item) => capitalize(item.payment_status),
    class: (item) => {
      const statusClasses = {
        'pending': 'text-yellow-600 dark:text-yellow-400',
        'processing': 'text-blue-600 dark:text-blue-400',
        'paid': 'text-green-600 dark:text-green-400',
        'cancelled': 'text-red-600 dark:text-red-400',
      };
      return statusClasses[item.payment_status] || '';
    }
  },
  {
    key: 'created_at',
    label: 'Date',
    format: (item) => formatDateTime(item.created_at)
  }
];

const deliveryColumns = [
  { key: 'id', label: 'ID', width: '80px' },
  {
    key: 'created_at',
    label: 'Date',
    format: (item) => formatDateTime(item.created_at)
  },
  { key: 'customer_name', label: 'Customer' },
  { key: 'business_name', label: 'Business' },
  {
    key: 'status',
    label: 'Status',
    class: (item) => {
      const statusClasses = {
        'pending': 'text-yellow-600 dark:text-yellow-400',
        'in_transit': 'text-blue-600 dark:text-blue-400',
        'delivered': 'text-green-600 dark:text-green-400',
        'failed': 'text-red-600 dark:text-red-400',
      };
      return statusClasses[item.status] || '';
    }
  },
];

// Fetch user details
const fetchUserDetails = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await api.get(`/api/admin/users/${userId}`);
    user.value = response.data.user;
    userStats.value = response.data.stats;

    // Update pagination if needed
    if (response.data.stats.ordersPagination) {
      ordersPagination.value = {...response.data.stats.ordersPagination};
    }

    if (response.data.stats.deliveryPagination) {
      deliveryPagination.value = response.data.stats.deliveryPagination;
    }
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to load user details';
    console.error('Error fetching user details:', err);
  } finally {
    loading.value = false;
  }
};

const fetchRecentOrders = async (page = 1) => {

  try {
    const response = await api.get(`/api/admin/users/${userId}/recent-orders?page=${page}`);
    recentOrders.value = response.data.recentOrders;
    ordersPagination.value = response.data.ordersPagination;
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to load recent orders';
    console.error('Error fetching recent orders:', err);
  }
}

const fetchRecentDeliveries = async (page = 1) => {

  try {
    const response = await api.get(`/api/admin/users/${userId}/recent-deliveries?page=${page}`);
    recentDeliveries.value = response.data.recentDeliveries;
    deliveryPagination.value = response.data.deliveryPagination;
  }
  catch (err) {
    error.value = err.response?.data?.message || 'Failed to load recent deliveries';
    console.error('Error fetching recent deliveries:', err);
  }
}

onMounted(async () => {
  await fetchUserDetails();
  if (user.value.role === USER_ROLES.BUSINESS) {
    await fetchRecentOrders(ordersPagination.value.current_page);
  }
  if (user.value.role === USER_ROLES.DRIVER) {
    await fetchRecentDeliveries(deliveryPagination.value.current_page);
  }
});
</script>
